<script lang="ts">
  /**
   * RelationshipsOverlay — faction reputations display.
   * Fetches real data from the engine via Tauri.
   */
  import { getFactionReps } from "$lib/api/invoke";
  import type { FactionRepInfo } from "$lib/api/invoke";

  let factions: FactionRepInfo[] = $state([]);
  let loading = $state(true);

  async function load(): Promise<void> {
    try {
      factions = await getFactionReps();
    } catch {
      factions = [];
    }
    loading = false;
  }

  load();

  function repBar(rep: number): string {
    const filled = rep + 5; // 0-10 scale
    const bar = "=".repeat(filled) + ".".repeat(10 - filled);
    return "[" + bar + "]";
  }

  function repLabel(rep: number): string {
    const sign = rep > 0 ? "+" : "";
    return sign + rep;
  }
</script>

<div class="rel-overlay">
  <h2 class="rel-overlay__title">RELATIONSHIPS (R)</h2>

  <div class="rel-overlay__border">+{"=".repeat(44)}+</div>

  <div class="rel-overlay__list">
    {#if loading}
      <span class="rel-overlay__empty">Loading...</span>
    {:else if factions.length === 0}
      <span class="rel-overlay__empty">No faction relationships yet.</span>
    {:else}
      {#each factions as faction}
        <div class="rel-overlay__entry">
          <span class="rel-overlay__name">{faction.name.padEnd(18, " ")}</span>
          <span class="rel-overlay__rep">{repLabel(faction.rep).padStart(3, " ")}</span>
          <span
            class="rel-overlay__bar"
            class:rel-overlay__bar--positive={faction.rep > 0}
            class:rel-overlay__bar--negative={faction.rep < 0}
            class:rel-overlay__bar--neutral={faction.rep === 0}
          >
            {repBar(faction.rep)}
          </span>
        </div>
      {/each}
    {/if}
  </div>

  <div class="rel-overlay__border">+{"=".repeat(44)}+</div>

  <p class="rel-overlay__hint">Reputation range: -5 (hostile) to +5 (allied)</p>
</div>

<style>
  .rel-overlay {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
  }

  .rel-overlay__title {
    color: var(--fg-bright);
    font-size: 1.1rem;
    letter-spacing: 0.15em;
  }

  .rel-overlay__border {
    color: var(--fg-dim);
    font-size: 0.85rem;
  }

  .rel-overlay__list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: 100%;
    max-width: 55ch;
  }

  .rel-overlay__entry {
    display: flex;
    gap: 0.5rem;
    font-size: 0.85rem;
    font-family: var(--font-mono);
    white-space: pre;
  }

  .rel-overlay__name {
    color: var(--fg);
  }

  .rel-overlay__rep {
    color: var(--fg-dim);
  }

  .rel-overlay__bar--positive {
    color: var(--fg-bright);
  }

  .rel-overlay__bar--negative {
    color: var(--accent);
  }

  .rel-overlay__bar--neutral {
    color: var(--fg-dim);
  }

  .rel-overlay__hint {
    color: var(--fg-dim);
    font-size: 0.75rem;
    margin-top: 0.5rem;
  }

  .rel-overlay__empty {
    color: var(--fg-dim);
    font-size: 0.85rem;
    text-align: center;
    padding: 1rem;
  }
</style>
